/**
 * Notification Actions
 *
 * Predefined action builders for each notification type.
 * These define what happens when a notification is clicked/tapped.
 */

import { FILTERABLE_STUDY_STATUSES } from "../study-status";
import type { NotificationAction } from "./types";
import { createDismissAction, createNavigateAction } from "./types";

// ============================================================================
// iOS Deep Link Builders
// ============================================================================

export const iOSDeepLinks = {
  invitation: (invitationId: string) => `terac://invitation/${invitationId}`,
  study: (studyId: string) => `terac://study/${studyId}`,
  earnings: () => "terac://earnings",
  balance: () => "terac://balance",
  home: () => "terac://home",
  me: () => "terac://me",
  orgMembers: (orgSlug: string) => `terac://org/${orgSlug}/members`,
} as const;

// ============================================================================
// Web Path Builders
// ============================================================================

export const webPaths = {
  acceptInvitation: (invitationId: string) =>
    `/accept-invitation/${invitationId}`,
  studyInvitationAccept: (invitationId: string) =>
    `/invitations/${invitationId}/accept`,
  applyStart: (recruitmentToken: string) => `/apply/start/${recruitmentToken}`,
  orgMembers: (orgSlug: string) => `/${orgSlug}/settings/members`,
  home: () => "/home",
  me: () => "/me",
  availability: (weekOf: string) => `/me/availability?week=${weekOf}`,
  earnings: () => "/me/earnings",
  opportunityHome: (studyId: string) => `/me/opportunities/${studyId}`,
  study: (orgSlug: string, projectSlug: string, studyId: string) =>
    `/${orgSlug}/${projectSlug}/opportunities/${studyId}`,
  studyOverview: (orgSlug: string, projectSlug: string, studyId: string) =>
    `/${orgSlug}/${projectSlug}/opportunities/${studyId}/overview`,
  studyCreationReview: (
    orgSlug: string,
    projectSlug: string,
    studyId: string,
  ) =>
    `/${orgSlug}/${projectSlug}/opportunities/create?id=${studyId}&step=review`,
  studySubmissions: (orgSlug: string, projectSlug: string, studyId: string) =>
    `/${orgSlug}/${projectSlug}/opportunities/${studyId}/submissions`,
  orgHome: (orgSlug: string) => `/${orgSlug}`,
  /**
   * `statuses` accepts raw strings because callers relay agent- or
   * caller-supplied values; anything the list page would ignore is dropped, and
   * an empty result emits no param at all so the page keeps falling back to the
   * researcher's saved cookie and then the default set.
   */
  orgOpportunities: (orgSlug: string, statuses?: readonly string[]) => {
    const accepted = [...new Set(statuses ?? [])].filter((status) =>
      (FILTERABLE_STUDY_STATUSES as readonly string[]).includes(status),
    );
    return accepted.length > 0
      ? `/${orgSlug}/opportunities?status=${accepted.join(",")}`
      : `/${orgSlug}/opportunities`;
  },
  orgFeasibility: (orgSlug: string) => `/${orgSlug}/feasibility`,
  orgFinance: (orgSlug: string) => `/${orgSlug}/settings/finance`,
  orgApiKeys: (orgSlug: string) => `/${orgSlug}/settings/api-keys`,
  projectOpportunities: (orgSlug: string, projectSlug: string) =>
    `/${orgSlug}/${projectSlug}/opportunities`,
  // Sits at project level, not under the study: the builder loads the draft from `?id`.
  studyDraftEditor: (orgSlug: string, projectSlug: string, studyId: string) =>
    `/${orgSlug}/${projectSlug}/opportunities/create?id=${studyId}`,
  studyRecruitment: (orgSlug: string, projectSlug: string, studyId: string) =>
    `/${orgSlug}/${projectSlug}/opportunities/${studyId}/recruitment`,
  studyTask: (orgSlug: string, projectSlug: string, studyId: string) =>
    `/${orgSlug}/${projectSlug}/opportunities/${studyId}/task`,
  studySettings: (orgSlug: string, projectSlug: string, studyId: string) =>
    `/${orgSlug}/${projectSlug}/opportunities/${studyId}/settings`,
} as const;

// ============================================================================
// Action Builders per Notification Type
// ============================================================================

/**
 * PLAN_REMINDER
 * Navigate to My availability on the week still to plan
 */
export function buildPlanReminderAction(weekOf: string): NotificationAction {
  return createNavigateAction(webPaths.availability(weekOf));
}

/**
 * ORGANIZATION_INVITATION_CREATED
 * Navigate to accept invitation page
 */
export function buildOrgInvitationCreatedAction(
  invitationId: string,
): NotificationAction {
  return createNavigateAction(
    webPaths.acceptInvitation(invitationId),
    iOSDeepLinks.invitation(invitationId),
  );
}

/**
 * ORGANIZATION_INVITATION_ACCEPTED
 * Navigate to org members page
 */
export function buildOrgInvitationAcceptedAction(
  orgSlug: string,
): NotificationAction {
  return createNavigateAction(
    webPaths.orgMembers(orgSlug),
    iOSDeepLinks.orgMembers(orgSlug),
  );
}

/**
 * ORGANIZATION_INVITATION_REVOKED
 * Dismiss only (no action needed)
 */
export function buildOrgInvitationRevokedAction(): NotificationAction {
  return createDismissAction();
}

/**
 * ORGANIZATION_JOIN_REQUESTED
 * Navigate to org members page, where the request is approved or denied
 */
export function buildOrgJoinRequestedAction(
  orgSlug: string,
): NotificationAction {
  return createNavigateAction(
    webPaths.orgMembers(orgSlug),
    iOSDeepLinks.orgMembers(orgSlug),
  );
}

/**
 * ORGANIZATION_JOIN_APPROVED
 * Navigate to the organization the requester was just added to
 */
export function buildOrgJoinApprovedAction(
  orgSlug: string,
): NotificationAction {
  return createNavigateAction(webPaths.orgHome(orgSlug), iOSDeepLinks.home());
}

/**
 * INTERVIEW_INVITATION_SENT
 * Navigate directly to the invitation accept page
 */
export function buildInterviewInvitationSentAction(
  invitationId: string,
): NotificationAction {
  return createNavigateAction(
    webPaths.studyInvitationAccept(invitationId),
    iOSDeepLinks.invitation(invitationId),
  );
}

/**
 * INTERVIEW_EARNINGS_POSTED
 * Navigate to earnings page
 */
export function buildEarningsPostedAction(): NotificationAction {
  return createNavigateAction(webPaths.earnings(), iOSDeepLinks.earnings());
}

/**
 * BUDGET_THRESHOLD_REACHED
 * Navigate to study detail
 */
export function buildBudgetThresholdAction(
  orgSlug: string,
  projectSlug: string,
  studyId: string,
): NotificationAction {
  return createNavigateAction(
    webPaths.study(orgSlug, projectSlug, studyId),
    iOSDeepLinks.study(studyId),
  );
}

/**
 * STUDY_APPROVAL_REQUESTED
 * Navigate to study overview (where approvals are shown)
 */
export function buildStudyApprovalRequestedAction(
  orgSlug: string,
  projectSlug: string,
  studyId: string,
): NotificationAction {
  return createNavigateAction(
    webPaths.studyOverview(orgSlug, projectSlug, studyId),
    iOSDeepLinks.study(studyId),
  );
}

/**
 * STUDY_APPROVAL_DECIDED
 * Navigate to study detail
 */
export function buildStudyApprovalDecidedAction(
  orgSlug: string,
  projectSlug: string,
  studyId: string,
): NotificationAction {
  return createNavigateAction(
    webPaths.study(orgSlug, projectSlug, studyId),
    iOSDeepLinks.study(studyId),
  );
}

/**
 * STUDY_ETA_SET / STUDY_ETA_UPDATED
 * Navigate to study detail
 */
export function buildStudyTurnaroundAction(
  orgSlug: string,
  projectSlug: string,
  studyId: string,
): NotificationAction {
  return createNavigateAction(
    webPaths.study(orgSlug, projectSlug, studyId),
    iOSDeepLinks.study(studyId),
  );
}

/**
 * STUDY_BOOKING_NEEDED
 * Navigate to the study's submissions page, where the researcher books the
 * expert-provided calendar slots.
 */
export function buildStudyBookingNeededAction(
  orgSlug: string,
  projectSlug: string,
  studyId: string,
): NotificationAction {
  return createNavigateAction(
    webPaths.studySubmissions(orgSlug, projectSlug, studyId),
    iOSDeepLinks.study(studyId),
  );
}

/** RESEARCHER_REVIEW_DIGEST - navigate to the organization's review queue. */
export function buildResearcherReviewDigestAction(
  orgSlug: string,
): NotificationAction {
  return createNavigateAction(webPaths.orgHome(orgSlug));
}

/**
 * INTERVIEW_BOOKED, researcher side
 * Navigate to the study's submissions page, where the booked slot is shown.
 */
export function buildInterviewBookedResearcherAction(
  orgSlug: string,
  projectSlug: string,
  studyId: string,
): NotificationAction {
  return createNavigateAction(
    webPaths.studySubmissions(orgSlug, projectSlug, studyId),
    iOSDeepLinks.study(studyId),
  );
}

/**
 * INTERVIEW_BOOKED, expert side
 * Navigate to the participant dashboard, which carries the booked-interview
 * card. No study deep link: the study's researcher-side pages are not theirs.
 */
export function buildInterviewBookedExpertAction(): NotificationAction {
  return createNavigateAction(webPaths.me(), iOSDeepLinks.me());
}

/**
 * OFFICE_HOURS_ANNOUNCED
 * The study's handbook, which holds the times and the join link. `null` for a
 * withdrawal: that reader may no longer be allowed to open the handbook, and a
 * notification should not lead to a page that turns them away. iOS has no
 * handbook screen, so it opens home either way.
 */
export function buildOfficeHoursAnnouncedAction(
  studyId: string | null,
): NotificationAction {
  return createNavigateAction(
    studyId ? webPaths.opportunityHome(studyId) : webPaths.me(),
    iOSDeepLinks.me(),
  );
}

/**
 * STUDY_STARTED
 * Navigate to study detail
 */
export function buildStudyStartedAction(
  orgSlug: string,
  projectSlug: string,
  studyId: string,
): NotificationAction {
  return createNavigateAction(
    webPaths.study(orgSlug, projectSlug, studyId),
    iOSDeepLinks.study(studyId),
  );
}

/**
 * FEASIBILITY_QUOTE_READY
 * Navigate to the study-creation review step, where the researcher sees the
 * quoted incentive and can launch.
 */
export function buildFeasibilityQuoteReadyAction(
  orgSlug: string,
  projectSlug: string,
  studyId: string,
): NotificationAction {
  return createNavigateAction(
    webPaths.studyCreationReview(orgSlug, projectSlug, studyId),
    iOSDeepLinks.study(studyId),
  );
}

/**
 * FEASIBILITY_QUOTE_READY, for a request that reached us outside the study
 * builder and so has no draft to review. Lands on the org's feasibility
 * requests page, where a priced request reads "Ready to launch".
 *
 * No iOS deep link: the requests page is web-only.
 */
export function buildFeasibilityQuoteReadyRequestsAction(
  orgSlug: string,
): NotificationAction {
  return createNavigateAction(webPaths.orgFeasibility(orgSlug));
}

/**
 * INTERVIEW_APPROVED
 * Navigate to earnings page to see payment
 */
export function buildInterviewApprovedAction(): NotificationAction {
  return createNavigateAction(webPaths.earnings(), iOSDeepLinks.earnings());
}

/**
 * INTERVIEW_AUTO_APPROVED
 * Navigate to earnings page to see payment
 */
export function buildInterviewAutoApprovedAction(): NotificationAction {
  return createNavigateAction(webPaths.earnings(), iOSDeepLinks.earnings());
}

/**
 * INTERVIEW_REJECTED
 * Navigate to earnings page to see balance impact
 */
export function buildInterviewRejectedAction(): NotificationAction {
  return createNavigateAction(webPaths.earnings(), iOSDeepLinks.earnings());
}

/**
 * PAYMENT_CLAWED_BACK
 * Navigate to earnings page to see balance impact
 */
export function buildPaymentClawedBackAction(): NotificationAction {
  return createNavigateAction(webPaths.earnings(), iOSDeepLinks.earnings());
}

/**
 * BALANCE_NEGATIVE
 * Navigate to earnings page to see balance
 */
export function buildBalanceNegativeAction(): NotificationAction {
  return createNavigateAction(webPaths.earnings(), iOSDeepLinks.earnings());
}

/**
 * COMPLETE_MATCH_PROFILE
 * Navigate to /me where the profile-completion popup surfaces missing fields.
 */
export function buildCompleteMatchProfileAction(): NotificationAction {
  return createNavigateAction(webPaths.me(), iOSDeepLinks.me());
}

/**
 * STUDY_COMPLETED
 * Navigate to study detail
 */
export function buildStudyCompletedAction(
  orgSlug: string,
  projectSlug: string,
  studyId: string,
): NotificationAction {
  return createNavigateAction(
    webPaths.study(orgSlug, projectSlug, studyId),
    iOSDeepLinks.study(studyId),
  );
}
